import { getHashValueUnsafe, getTokenValue, getTokenValueUnsafe } from '@/services/token';
import { Query, stringify } from '@/services/api';
import { StatusCodes } from 'http-status-codes';
import { fileTypes } from '@/services/assets-api/assetsValidation';

export enum ImageVersions {
  OPTI = 'opti',
  THUMB = 'thumb',
  ORIG = 'orig',
}

export enum AssetsNamespaces {
  inspections = 'inspections',
  issues = 'issues',
  templates = 'templates',
  checklists = 'checklists'
}

export enum AssetsSubNamespaces {
  questions = 'questions',
  images = 'images'
}

export interface AssetsResponse {
  success: boolean;
  errors?: Array<string>;
  code: number;
  id?: string;
  fileName?: string;
}

export type AssetsResponseBulk = Array<AssetsResponse>;

const assetsUrl = process.env.VUE_APP_ASSETS_SERVICE_URL;

const assetsImageUrl = `${assetsUrl}/image`;
const uploadUrl = `${assetsImageUrl}/upload`;
const uploadBulkUrl = `${assetsImageUrl}/upload-bulk`;
const removeBulkUrl = `${assetsImageUrl}/remove-bulk`;

const assetsDocumentUrl = `${assetsUrl}/document`;
const uploadDocumentUrl = `${assetsDocumentUrl}/upload`;

export const imageUrlAuthorized = (type : fileTypes = 'image') => {
  return (fileId: string, version?: ImageVersions) => {
    // read it every time.
    // on FE token comes from a current token. on BE generation from a BE variable window.beToken
    //@ts-ignore
    const token = window.beToken || getTokenValueUnsafe() || getHashValueUnsafe();

    const fileIdUrl = `${type === 'image' ? assetsImageUrl : assetsDocumentUrl}/${fileId}`;
    const queryParams: Query = { token };
    if (version) {
      queryParams.version = version;
    }

    return `${fileIdUrl}?${stringify(queryParams)}`;
  };
};

export const fileUrlWithoutToken = (fileId: string) => {
  return `${assetsDocumentUrl}/${fileId}`;
};

export const imageUrl = imageUrlAuthorized();
export const documentUrl = imageUrlAuthorized('file');

const prepareAuthorizedHeaders = async (): Promise<Headers> => {
  const headers = new Headers();
  const token = await getTokenValue();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
};

/**
 * @param fileId providing fileId will update the image by this id, but please don't
 */
export const uploadImage = async (
  {
    file,
    namespace,
    subnamespace,
    fileId,
  }: { file: File; namespace: string; subnamespace: string; fileId: string | undefined },
): Promise<AssetsResponse | boolean> => {
  const headers = await prepareAuthorizedHeaders();

  const formData = new FormData();
  formData.append('file', file, file.name);
  formData.append('namespace', namespace);
  formData.append('subnamespace', subnamespace);

  if (fileId) {
    formData.append('fileId', fileId);
  }

  const requestOptions: RequestInit = {
    method: 'POST',
    headers,
    body: formData,
    redirect: 'follow',
  };

  try {
    const response = await fetch(uploadUrl, requestOptions);

    if (response.status !== StatusCodes.OK) {
      console.error(`Upload image status: ${response.status}`);
      return false;
    }

    // reads stream
    return response.json();
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const uploadImageMany = async (
  {
    files,
    namespace,
    subnamespace,
  }: { files: Array<File>; namespace: string; subnamespace: string },
): Promise<AssetsResponseBulk | boolean> => {
  const headers = await prepareAuthorizedHeaders();

  const formData = new FormData();
  files.forEach(file => formData.append(file.name, file, file.name));
  formData.append('namespace', namespace);
  formData.append('subnamespace', subnamespace);

  const requestOptions: RequestInit = {
    method: 'POST',
    headers,
    body: formData,
    redirect: 'follow',
  };

  try {
    const response = await fetch(uploadBulkUrl, requestOptions);

    if (response.status !== StatusCodes.OK) {
      console.error(`Upload image status: ${response.status}`);
      return false;
    }

    // reads stream
    return response.json();
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const removeImage = async (
  fileId: string,
): Promise<boolean> => {
  const headers = await prepareAuthorizedHeaders();

  const formData = new FormData();

  const requestOptions: RequestInit = {
    method: 'DELETE',
    headers,
    body: formData,
    redirect: 'follow',
  };

  const response = await fetch(imageUrl(fileId), requestOptions);

  if (response.status !== StatusCodes.NO_CONTENT) {
    console.error(`Remove image status: ${response.status}`);
    return false;
  }

  return true;
};

export const removeImageMany = async (
  fileIds: Array<string>,
): Promise<boolean> => {
  const headers = await prepareAuthorizedHeaders();

  const formData = new FormData();
  formData.set('ids', fileIds.join(','));

  const requestOptions: RequestInit = {
    method: 'DELETE',
    headers,
    body: formData,
    redirect: 'follow',
  };

  const response = await fetch(removeBulkUrl, requestOptions);

  if (response.status !== StatusCodes.NO_CONTENT) {
    console.error(`Remove image status: ${response.status}`);
    return false;
  }

  return true;
};

export const getImage = async (
  fileId: string, version?: ImageVersions,
): Promise<Blob | null> => {
  const headers = await prepareAuthorizedHeaders();

  const requestOptions: RequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
  };

  const response = await fetch(imageUrl(fileId, version), requestOptions);

  if (response.status !== StatusCodes.OK) {
    console.error(`Get image status: ${response.status}`);
    return null;
  }

  const image = await response.blob();

  return image;
};

export const uploadDocument = async (
  {
    file,
    namespace,
    subnamespace,
    fileId,
  }: { file: File; namespace: string; subnamespace: string; fileId: string | undefined },
): Promise<AssetsResponse | boolean> => {
  const headers = await prepareAuthorizedHeaders();

  const formData = new FormData();
  formData.append('file', file, file.name);
  formData.append('namespace', namespace);
  formData.append('subnamespace', subnamespace);

  if (fileId) {
    formData.append('fileId', fileId);
  }

  const requestOptions: RequestInit = {
    method: 'POST',
    headers,
    body: formData,
    redirect: 'follow',
  };

  try {
    const response = await fetch(uploadDocumentUrl, requestOptions);

    if (response.status !== StatusCodes.OK) {
      console.error(`Upload document status: ${response.status}`);
      return false;
    }

    // reads stream
    return response.json();
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const removeDocument = async (
  fileId: string,
): Promise<boolean> => {
  const headers = await prepareAuthorizedHeaders();

  const formData = new FormData();

  const requestOptions: RequestInit = {
    method: 'DELETE',
    headers,
    body: formData,
    redirect: 'follow',
  };

  formData.append('fileId', fileId);

  const response = await fetch(documentUrl(fileId), requestOptions);

  if (response.status !== StatusCodes.NO_CONTENT) {
    console.error(`Remove image status: ${response.status}`);
    return false;
  }

  return true;
};
